import { sameRate, toFixedDecimal, trimRate } from '@mms/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useState } from 'react';
import { Alert, Button, Form, Modal, Table } from 'react-bootstrap';
import { z } from 'zod';
import { ApiError, api } from '../api/client';
import type { Row } from '../api/types';
import { useCan } from '../auth/AuthContext';
import { dateReq, selectReq, toId } from '../forms/zod';
import type { Column } from '../ui/DataTable';
import { useToast } from '../ui/feedback';
import { fmtDate } from '../ui/format';
import { toOptions, useLatestRates, useLookup } from '../ui/lookups';
import { FieldRenderer, invalidateData, type CrudConfig, type FieldDef, type ScreenCtx } from './CrudScreen';

/**
 * Exchange rates (redesigned 2026-10-16). Every rate is the amount of the selected currency per 1 unit of the BASE currency (the one flagged on the Currency
 * master); the retail rate is the one invoices use. The screen has: Today's rates (one line per currency: retail, wholesale, spread, change, and whether it
 * was updated today), Update all rates (every changed rate of the day in one go), and the Rate history with filters. Saving a rate for a currency and day
 * that already has one replaces it.
 */
const r4 = (v: unknown) => toFixedDecimal(String(v), 4);
const pctText = (n: number) => `${n >= 0 ? '+' : ''}${n.toFixed(2)}%`;
const changePct = (cur: unknown, prev: unknown): number | null => (Number(prev) > 0 && Number(cur) > 0 ? ((Number(cur) - Number(prev)) / Number(prev)) * 100 : null);
const spreadPct = (retail: unknown, wholesale: unknown): number | null => (Number(retail) > 0 && Number(wholesale) > 0 ? ((Number(wholesale) - Number(retail)) / Number(retail)) * 100 : null);
const todayIso = () => dayjs().format('YYYY-MM-DD');

/** "▲ +0.09%" in green, "▼ -0.41%" in red, a dash when there is nothing to compare with. */
function Change({ cur, prev, arrow = true }: { cur: unknown; prev: unknown; arrow?: boolean }) {
  const c = changePct(cur, prev);
  if (c === null) return <span className="text-body-tertiary">—</span>;
  return <span className={c >= 0 ? 'text-success' : 'text-danger'}>{arrow ? (c >= 0 ? '▲ ' : '▼ ') : ''}{pctText(c)}</span>;
}

const rateField = (msg: string) => z.string().trim().regex(/^\d+(\.\d{1,8})?$/, msg).refine((v) => Number(v) > 0, msg);
const rateSchema = z.object({
  currencyId: selectReq('Select the currency'),
  rateDate: dateReq.refine((v) => v <= todayIso(), "The date can't be in the future"),
  rate1: rateField('Enter the retail rate'),
  rate2: rateField('Enter the wholesale rate'),
});

/** Currencies that have a rate: active, not the base. */
function useRatedCurrencies() {
  const currencies = useLookup('currencies');
  return (currencies.data ?? []).filter((c) => !c.isBase);
}

// ---------------------------------------------------------------- Today's rates
function TodaysRates({ ctx }: { ctx: ScreenCtx }) {
  const canCreate = useCan('exchange-rates', 'create');
  const rated = useRatedCurrencies();
  const latest = useLatestRates();
  const byCurrency = new Map((latest.data ?? []).map((l) => [l.currencyId, l]));
  const today = todayIso();
  return (
    <div className="card mb-3" data-testid="todays-rates">
      <div className="card-header d-flex align-items-center fw-semibold">
        Today&apos;s rates<span className="ms-auto fw-normal small text-body-secondary">As of {fmtDate(today)}</span>
      </div>
      <div className="table-responsive">
        <Table size="sm" className="mb-0 align-middle">
          <thead>
            <tr><th>Currency</th><th className="text-end">Retail</th><th className="text-end">Wholesale</th><th className="text-end">Spread</th><th className="text-end">Change</th><th>Status</th><th><span className="visually-hidden">Action</span></th></tr>
          </thead>
          <tbody>
            {rated.map((c) => {
              const l = byCurrency.get(c.id as number);
              const stale = !!l && String(l.rateDate).slice(0, 10) !== today;
              const spread = l ? spreadPct(l.rate1, l.rate2) : null;
              const open = () => ctx.openCreate({
                currencyId: String(c.id), rateDate: today, ...(l ? { rate1: trimRate(String(l.rate1)), rate2: trimRate(String(l.rate2)) } : {}),
              });
              return (
                <tr key={String(c.id)}>
                  <td className="fw-semibold">{c.code}</td>
                  {l ? (
                    <>
                      <td className="text-end">{r4(l.rate1)}</td>
                      <td className="text-end">{r4(l.rate2)}</td>
                      <td className="text-end">{spread === null ? '—' : `${spread.toFixed(2)}%`}</td>
                      <td className="text-end"><Change cur={l.rate1} prev={l.prevRate1} /></td>
                      <td>
                        {stale
                          ? <><span className="badge text-bg-warning">Not updated today</span><div className="small text-body-secondary">Last {fmtDate(String(l.rateDate))}</div></>
                          : <span className="badge text-bg-success">Updated today</span>}
                      </td>
                    </>
                  ) : <td colSpan={5} className="small text-body-secondary">No rate yet</td>}
                  <td className="text-end">
                    {canCreate && <Button size="sm" variant="outline-secondary" onClick={open} aria-label={`${!l ? 'Add rate' : stale ? 'Update' : 'Edit'} ${c.code}`}>{!l ? 'Add rate' : stale ? 'Update' : 'Edit'}</Button>}
                  </td>
                </tr>
              );
            })}
            {rated.length === 0 && <tr><td colSpan={7} className="text-center text-body-secondary py-3">No currencies to quote yet.</td></tr>}
          </tbody>
        </Table>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- Update all rates
function BulkUpdate() {
  const canCreate = useCan('exchange-rates', 'create');
  const qc = useQueryClient();
  const toast = useToast();
  const rated = useRatedCurrencies();
  const latest = useLatestRates();
  const [open, setOpen] = useState(false);
  const [values, setValues] = useState<Record<string, { r: string; w: string }>>({});
  const [message, setMessage] = useState<{ kind: 'warning' | 'danger' | 'info'; text: string } | null>(null);
  const [bad, setBad] = useState<Set<string>>(new Set());
  const byCurrency = new Map((latest.data ?? []).map((l) => [l.currencyId, l]));
  const today = todayIso();

  const original = (id: number) => { const l = byCurrency.get(id); return { r: l ? trimRate(String(l.rate1)) : '', w: l ? trimRate(String(l.rate2)) : '' }; };
  const start = () => {
    setValues(Object.fromEntries(rated.map((c) => [String(c.id), original(c.id as number)])));
    setMessage(null);
    setBad(new Set());
    setOpen(true);
  };
  const valueOf = (id: number) => values[String(id)] ?? original(id);
  const changed = (id: number, k: 'r' | 'w') => !sameRate(valueOf(id)[k], original(id)[k]) && (valueOf(id)[k] !== original(id)[k]);

  const save = useMutation({
    mutationFn: (rates: { currencyId: number; rate1: string; rate2: string }[]) => api.post<Row>('/exchange-rates/bulk', { rates }),
    onSuccess: async (res) => {
      await invalidateData(qc);
      setOpen(false);
      toast.success(`${res.saved} rate${res.saved === 1 ? '' : 's'} saved for ${fmtDate(String(res.rateDate))}.`);
    },
    onError: (e) => setMessage({ kind: 'danger', text: e instanceof ApiError ? e.message : 'Could not save the rates' }),
  });

  // "Get live rates": the market rate of every currency, put into the Retail and Wholesale boxes for review. Nothing is saved until Save changed rates.
  const live = useMutation({
    mutationFn: () => api.get<{ base: string; asOf: string | null; source: string; rates: Record<string, string>; missing: string[] }>('/exchange-rates/market'),
    onSuccess: (res) => {
      setValues((cur) => {
        const next = { ...cur };
        for (const c of rated) {
          const v = res.rates[String(c.code)];
          if (v) { const rounded = toFixedDecimal(v, 4); next[String(c.id)] = { r: rounded, w: rounded }; }
        }
        return next;
      });
      setBad(new Set());
      const missing = res.missing.length ? ` No live rate for ${res.missing.join(', ')}: left as it was.` : '';
      setMessage({
        kind: 'info',
        text: `Live rates from ${res.source}${res.asOf ? ` as of ${res.asOf}` : ''}, per 1 ${res.base}. Retail and Wholesale are both set to the market rate: adjust them if you quote a spread, then Save changed rates.${missing}`,
      });
    },
    onError: (e) => setMessage({ kind: 'danger', text: e instanceof ApiError ? e.message : 'Could not get the live rates' }),
  });

  const submit = () => {
    const rates: { currencyId: number; rate1: string; rate2: string }[] = [];
    const invalid = new Set<string>();
    const ok = (v: string) => /^\d+(\.\d{1,8})?$/.test(v.trim()) && Number(v) > 0;
    for (const c of rated) {
      const id = c.id as number;
      const v = valueOf(id);
      const o = original(id);
      if (!o.r && !v.r.trim() && !o.w && !v.w.trim()) continue; // no rate yet and none entered: nothing to save
      if (!ok(v.r)) invalid.add(`${id}-r`);
      if (!ok(v.w)) invalid.add(`${id}-w`);
      if (ok(v.r) && ok(v.w) && (changed(id, 'r') || changed(id, 'w'))) rates.push({ currencyId: id, rate1: v.r.trim(), rate2: v.w.trim() });
    }
    setBad(invalid);
    if (invalid.size) { setMessage({ kind: 'danger', text: 'Enter a rate greater than 0 in every highlighted field.' }); return; }
    if (!rates.length) { setMessage({ kind: 'warning', text: 'No rates were changed.' }); return; }
    setMessage(null);
    save.mutate(rates);
  };

  if (!canCreate) return null;
  return (
    <>
      <Button variant="outline-secondary" onClick={start}><i className="bi bi-list-ul me-1" aria-hidden />Update all rates</Button>
      <Modal show={open} onHide={() => setOpen(false)} size="xl" backdrop="static" centered scrollable>
        <Modal.Header closeButton><Modal.Title as="h2" className="h5">Update all rates for {fmtDate(today)}</Modal.Title></Modal.Header>
        <Modal.Body>
          <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
            <p className="small text-body-secondary mb-0 me-auto">Change only the rates that moved. Rows you edit are highlighted and saved with today&apos;s date.</p>
            <Button size="sm" variant="outline-primary" onClick={() => { setMessage(null); live.mutate(); }} disabled={live.isPending}>
              <i className="bi bi-cloud-download me-1" aria-hidden />{live.isPending ? 'Getting rates…' : 'Get live rates'}
            </Button>
          </div>
          <div className="table-responsive">
            <Table size="sm" className="align-middle">
              <thead>
                <tr><th>Currency</th><th className="text-end">Previous retail</th><th className="text-end" style={{ width: 140 }}>Retail</th><th className="text-end">Previous wholesale</th><th className="text-end" style={{ width: 140 }}>Wholesale</th><th className="text-end">Change</th></tr>
              </thead>
              <tbody>
                {rated.map((c) => {
                  const id = c.id as number;
                  const v = valueOf(id);
                  const o = original(id);
                  const input = (k: 'r' | 'w') => (
                    <Form.Control
                      size="sm" className={`text-end ${changed(id, k) ? 'border-primary' : ''}`} inputMode="decimal" autoComplete="off" isInvalid={bad.has(`${id}-${k}`)}
                      aria-label={`${c.code} ${k === 'r' ? 'retail' : 'wholesale'} rate`} value={v[k]}
                      onChange={(e) => setValues((cur) => ({ ...cur, [String(id)]: { ...valueOf(id), [k]: e.target.value } }))}
                    />
                  );
                  return (
                    <tr key={String(id)} data-testid={`bulk-${c.code}`}>
                      <td className="fw-semibold">{c.code}</td>
                      <td className="text-end small text-body-secondary">{o.r ? r4(o.r) : '—'}</td>
                      <td>{input('r')}</td>
                      <td className="text-end small text-body-secondary">{o.w ? r4(o.w) : '—'}</td>
                      <td>{input('w')}</td>
                      <td className="text-end small">{changed(id, 'r') ? <Change cur={v.r} prev={o.r} /> : '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </div>
          {message && <Alert variant={message.kind} className="py-2 mb-0" role="alert">{message.text}</Alert>}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={submit} disabled={save.isPending}>{save.isPending ? 'Saving…' : 'Save changed rates'}</Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

// ---------------------------------------------------------------- the screen
export const exchangeRatesConfig: CrudConfig = {
  title: 'Exchange rates', singular: 'exchange rate', icon: 'graph-up-arrow', menu: 'exchange-rates', endpoint: '/exchange-rates', update: 'patch',
  dateRange: true, compactActions: true,
  schema: rateSchema,
  defaults: () => ({ currencyId: '', rateDate: todayIso(), rate1: '', rate2: '' }),
  toForm: (r) => ({ currencyId: String(r.currencyId), rateDate: String(r.rateDate).slice(0, 10), rate1: trimRate(String(r.rate1)), rate2: trimRate(String(r.rate2)) }),
  toPayload: (v) => ({ currencyId: toId(v.currencyId), rateDate: v.rateDate, rate1: v.rate1.trim(), rate2: v.rate2.trim() }),
  rowLabel: (r) => `${r.currencyCode} ${fmtDate(r.rateDate)}`,
  initialSort: { key: 'date', dir: 'desc' },
  useSubtitle: () => {
    const all = useLookup('currencies', { all: 1 });
    const base = (all.data ?? []).find((c) => c.isBase);
    const code = (base?.code as string | undefined) ?? 'the base currency';
    return <>Base currency: <b>{base ? `${code}` : '—'}</b>{base ? <> – {String(base.name)}</> : null}. Every rate is the amount of the selected currency per 1 {code}.</>;
  },
  useAbove: (ctx) => <TodaysRates ctx={ctx} />,
  useHeaderActions: () => <BulkUpdate />,
  useTable: () => {
    const all = useLookup('currencies', { all: 1 });
    const columns: Column[] = [
      { key: 'rateDate', header: 'Date', sortKey: 'date', className: 'text-nowrap', render: (r) => fmtDate(r.rateDate) },
      { key: 'currencyCode', header: 'Currency', sortKey: 'currency', className: 'fw-semibold' },
      { key: 'rate1', header: 'Retail', sortKey: 'retail', align: 'end', render: (r) => r4(r.rate1) },
      { key: 'rate2', header: 'Wholesale', sortKey: 'wholesale', align: 'end', hideOnMobile: true, render: (r) => r4(r.rate2) },
      { key: 'spread', header: 'Spread', align: 'end', hideOnMobile: true, render: (r) => { const s = spreadPct(r.rate1, r.rate2); return s === null ? '—' : `${s.toFixed(2)}%`; } },
      { key: 'change', header: 'Change', align: 'end', render: (r) => <Change cur={r.rate1} prev={r.prevRate1} arrow={false} /> },
    ];
    return {
      columns,
      filters: [{ param: 'currencyId', label: 'Currency', options: toOptions((all.data ?? []).filter((c) => !c.isBase), (c) => c.code as string) }],
    };
  },
  useForm: (form, { mode, row, readOnly }) => {
    const all = useLookup('currencies', { all: 1 });
    const active = useRatedCurrencies();
    const currencyId = String(form.watch('currencyId') ?? '');
    const rateDate = String(form.watch('rateDate') ?? '');
    const r1 = String(form.watch('rate1') ?? '').trim();
    const r2 = String(form.watch('rate2') ?? '').trim();
    const base = (all.data ?? []).find((c) => c.isBase);
    const baseCode = (base?.code as string | undefined) ?? 'the base currency';
    const selected = (all.data ?? []).find((c) => String(c.id) === currencyId);
    const validDate = /^\d{4}-\d{2}-\d{2}$/.test(rateDate);

    // the rate before this date (for "Previous rate" and "Change") and any rate already saved for this currency and day (it would be replaced)
    const prev = useQuery({
      queryKey: ['rate-prev', currencyId, rateDate],
      enabled: !!currencyId && validDate,
      queryFn: async () => (await api.get<{ data: Row[] }>('/exchange-rates', { currencyId, to: dayjs(rateDate).subtract(1, 'day').format('YYYY-MM-DD'), sort: 'date', dir: 'desc', pageSize: 1 })).data[0] ?? null,
    });
    const existing = useQuery({
      queryKey: ['rate-same-day', currencyId, rateDate],
      enabled: !!currencyId && validDate,
      queryFn: async () => (await api.get<{ data: Row[] }>('/exchange-rates', { currencyId, from: rateDate, to: rateDate, pageSize: 1 })).data[0] ?? null,
    });

    const retail = Number(r1) > 0 ? Number(r1) : 0;
    const wholesale = Number(r2) > 0 ? Number(r2) : 0;
    const spread = retail && wholesale ? spreadPct(retail, wholesale) : null;
    const change = prev.data && retail ? changePct(retail, prev.data.rate1) : null;
    const info: [string, string][] = [];
    if (prev.data) info.push([`Previous rate (${fmtDate(String(prev.data.rateDate))})`, `Retail ${r4(prev.data.rate1)} · Wholesale ${r4(prev.data.rate2)}`]);
    if (change !== null) info.push(['Change from previous', `${change >= 0 ? '▲ ' : '▼ '}${pctText(change)}`]);
    if (spread !== null) info.push(['Spread', `${spread.toFixed(2)}% (wholesale vs retail)`]);
    if (retail && selected) {
      info.push(['Preview', `1 ${baseCode} = ${r4(retail)} ${selected.code} · 1,000 ${baseCode} = ${(1000 * retail).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${selected.code} · 1 ${selected.code} = ${(1 / retail).toFixed(6)} ${baseCode}`]);
    }
    const warnings: string[] = [];
    if (change !== null && Math.abs(change) > 2) warnings.push(`Large change (${pctText(change)}) from the previous rate. Check the figure before saving.`);
    if (spread !== null && Math.abs(spread) > 3) warnings.push('Retail and wholesale differ by more than 3%. Check both figures.');
    const replaces = !!existing.data && existing.data.id !== row?.id;

    const field = (def: FieldDef, col = 6) => <FieldRenderer form={form} field={{ ...def, col }} readOnly={readOnly} />;
    const options = toOptions(
      [...active, ...(mode === 'edit' && selected && selected.isBase !== true && !active.some((c) => c.id === selected.id) ? [selected] : [])],
      (c) => `${c.code} – ${c.name}`,
    );
    const body = (
      <>
        {replaces && <Alert variant="warning" className="py-2">A rate for {String(selected?.code)} on {fmtDate(rateDate)} already exists. Saving will replace it.</Alert>}
        <div className="row">
          {field({ name: 'currencyId', label: 'Currency', type: 'select', required: true, options, placeholder: 'Select…', help: `Rates are quoted against the base currency, ${baseCode}` })}
          {field({ name: 'rateDate', label: 'Date', type: 'date', required: true })}
          {field({ name: 'rate1', label: 'Retail rate', type: 'money', required: true, help: 'Invoices use the retail rate' })}
          {field({ name: 'rate2', label: 'Wholesale rate', type: 'money', required: true })}
        </div>
        {info.length > 0 && (
          <div className="rounded bg-body-tertiary px-3 py-2 small mb-3" data-testid="rate-info" style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '4px 14px' }}>
            {info.map(([k, v]) => <><span key={`${k}-k`} className="text-body-secondary">{k}</span><span key={`${k}-v`}>{v}</span></>)}
          </div>
        )}
        {warnings.map((w) => <Alert key={w} variant="warning" className="py-2 mb-2">{w}</Alert>)}
      </>
    );
    return { body, fields: [] };
  },
};
